/* ==========================================================
   SOLSTICE — RECONCILIACION DE TIER DE SUSCRIPCION v1.0

   Problema que resuelve:
   El selector de producto calcula el precio por volumen (tier) con las
   unidades de UN solo submit. Si el cliente agrega los sabores por
   separado — o cambia cantidades en el carrito — el carrito queda con
   2 unidades pero lineas al precio de 1 unidad (1049 en vez de 999).

   Solucion:
   Despues de cualquier mutacion del carrito, se cuentan las unidades de
   proteina en suscripcion y se reasigna a cada linea el selling plan del
   tier correcto, respetando la frecuencia que cada linea ya tenia.

   Solo toca lineas cuyo selling plan pertenece al grupo de Appstle
   definido en snippets/subscription-config.liquid. Las compras unicas y
   cualquier otra suscripcion quedan intactas.
   ========================================================== */

(function () {
  'use strict';

  const CFG = window.SOLSTICE_SUB;
  if (!CFG || !CFG.plans) return; // sin config no hay nada que reconciliar

  const THRESHOLD = CFG.tierThreshold || 2;
  const MAX_PASSES = 6; // guarda contra bucles: cada pasada corrige una linea

  /* ── indice inverso: plan id -> { tier, freq } ────────── */

  const PLAN_INDEX = {};
  Object.keys(CFG.plans).forEach((tier) => {
    Object.keys(CFG.plans[tier]).forEach((freq) => {
      PLAN_INDEX[String(CFG.plans[tier][freq])] = { tier: tier, freq: freq };
    });
  });

  /* ── helpers ─────────────────────────────────────────── */

  const planIdOf = (line) => {
    const alloc = line.selling_plan_allocation;
    const plan = alloc && alloc.selling_plan;
    return plan && plan.id ? String(plan.id) : null;
  };

  // Una linea nos pertenece si su selling plan esta en el grupo de proteinas
  const isOurLine = (line) => {
    const id = planIdOf(line);
    return !!(id && PLAN_INDEX[id]);
  };

  const getCart = () =>
    fetch('/cart.js', { headers: { 'X-Solstice-Internal': '1' } }).then((r) => r.json());

  const changeLine = (key, quantity, sellingPlan) =>
    fetch('/cart/change.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Solstice-Internal': '1'
      },
      body: JSON.stringify({ id: key, quantity: quantity, selling_plan: sellingPlan })
    }).then((r) => r.json());

  /* ── reconciliacion ──────────────────────────────────────
     Se corrige UNA linea por pasada y se vuelve a leer el carrito:
     cambiar el selling plan puede alterar la key de la linea, asi que
     las keys de una lectura previa dejan de ser confiables.          */

  let running = false;

  const reconcile = async () => {
    if (running) return false;
    running = true;
    let changed = false;

    try {
      for (let pass = 0; pass < MAX_PASSES; pass++) {
        const cart = await getCart();
        const lines = (cart.items || []).filter(isOurLine);
        if (!lines.length) break;

        const units = lines.reduce((sum, l) => sum + l.quantity, 0);
        const tier = units >= THRESHOLD ? '2+' : '1';

        // Primera linea cuyo plan no corresponde al tier vigente
        let target = null;
        let desired = null;
        for (const line of lines) {
          const freq = PLAN_INDEX[planIdOf(line)].freq;
          const want = CFG.plans[tier] && CFG.plans[tier][freq];
          if (want && String(want) !== planIdOf(line)) {
            target = line;
            desired = want;
            break;
          }
        }

        if (!target) break; // carrito consistente

        const res = await changeLine(target.key, target.quantity, desired);
        if (res && res.status) {
          console.error('[Solstice] no se pudo ajustar el tier:', res.message || res.description);
          break;
        }
        changed = true;
      }
    } catch (e) {
      console.error('[Solstice] reconciliacion de tier:', e);
    } finally {
      running = false;
    }

    if (changed) {
      notifyUI();
    } else {
      // Carrito ya consistente: se libera el cupo de recargas.
      try { sessionStorage.removeItem(RELOAD_KEY); } catch (e) {}
    }
    return changed;
  };

  /* ── refresco de UI ──────────────────────────────────────
     El header escucha cart-updated correctamente. La pagina de carrito
     tiene el listener mal enlazado en el tema (updateCart sin ejecutar),
     asi que ahi se recarga para que se vean los precios nuevos.       */

  const RELOAD_KEY = 'slc_tier_reloads';
  const RELOAD_CAP = 2; // si no converge, mejor precio raro que recarga infinita

  const notifyUI = () => {
    window.dispatchEvent(new Event('cart-updated'));
    if (!/^\/cart/.test(window.location.pathname)) return;

    // La pagina de carrito no re-renderiza sola (listener mal enlazado en el
    // tema), asi que se recarga. El tope evita un bucle si alguna linea nunca
    // llega a quedar consistente.
    let n = 0;
    try {
      n = parseInt(sessionStorage.getItem(RELOAD_KEY) || '0', 10) || 0;
      if (n >= RELOAD_CAP) return;
      sessionStorage.setItem(RELOAD_KEY, String(n + 1));
    } catch (e) {
      return; // sin sessionStorage no arriesgamos el bucle
    }
    window.location.reload();
  };

  /* ── disparadores ────────────────────────────────────────
     Se engancha window.fetch igual que solstice-bonus.js y se ignoran
     las llamadas internas (propias y del modulo de bonus) para no
     realimentar el ciclo.                                             */

  const hookFetch = () => {
    const orig = window.fetch;
    window.fetch = function (...args) {
      const url = String(args[0] || '');
      const opts = args[1] || {};
      const hdrs = opts.headers || {};
      const isInternal =
        hdrs['X-Solstice-Internal'] === '1' ||
        (hdrs instanceof Headers && hdrs.get('X-Solstice-Internal') === '1');
      const isMutation = /\/cart\/(add|change|update|clear)/.test(url);

      return orig.apply(this, args).then((res) => {
        if (isMutation && !isInternal && res.ok) {
          setTimeout(reconcile, 350); // despues del refresco propio del tema
        }
        return res;
      });
    };
  };

  const init = () => {
    hookFetch();
    reconcile(); // corrige carritos que ya venian mal de sesiones previas
    window.SolsticeSubscriptionTier = { reconcile: reconcile };
  };

  document.readyState === 'loading'
    ? document.addEventListener('DOMContentLoaded', init)
    : init();
})();
